import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import type { ScheduleProjectSummary } from '../../../../types/workflows';
import type { MilestoneRow } from '../../../../AppSettings/services/MilestoneService';
import ScheduleTooltip from '../Tooltip/ScheduleTooltip';
import './ScheduleTimeline.css';

export interface ScheduleTimelineProps {
  projects: ScheduleProjectSummary[];
  onProjectClick?: (project: ScheduleProjectSummary) => void;
  colorMode?: 'status' | 'discipline' | 'owner';
  zoomLevel?: 'week' | 'month' | 'quarter';
  onZoomChange?: (zoom: 'week' | 'month' | 'quarter') => void;
  milestonesByProject?: Record<string, MilestoneRow[]>;
  isLoading?: boolean;
}

interface GanttDataItem {
  projectId: string;
  projectName: string;
  laneId: string;
  laneLabel: string;
  start: Date;
  end: Date;
  startValue: number;
  endValue: number;
  progress: number;
  color: string;
  status?: string | null;
  milestoneName?: string;
  isOverall?: boolean;
  isOverallBackground?: boolean;
  isOverallForeground?: boolean;
  project: ScheduleProjectSummary;
}

const FALLBACK_DURATION_DAYS = 14;

const toValidDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Derive start/end dates even when API dates are missing.
 * Now builds bars per milestone when provided; otherwise falls back to project span.
 */
const transformToGanttData = (
  projects: ScheduleProjectSummary[],
  milestonesByProject: Record<string, MilestoneRow[]> | undefined,
  colorMode: 'status' | 'discipline' | 'owner' = 'status',
  collapsed?: Set<string>
): GanttDataItem[] => {
  const rows: GanttDataItem[] = [];

  projects.forEach((project) => {
    const milestones = milestonesByProject?.[project.projectId];

    let projectStart: Date | null = null;
    let projectEnd: Date | null = null;
    let milestoneCompletion = { done: 0, total: 0 };
    const milestoneRows: GanttDataItem[] = [];

    // If milestone rows exist, create one bar per milestone
    if (milestones && milestones.length > 0) {
      milestones.forEach((m, idx) => {
        const planned = toValidDate((m as any).plannedDate || (m as any).planned_date);
        const actual = toValidDate((m as any).actualDate || (m as any).actual_date);
        const durationDays =
          (m as any).durationDays ??
          (m as any).duration_days ??
          FALLBACK_DURATION_DAYS;

        const startDate = planned || actual || new Date();
        let endDate = actual;
        if (!endDate || endDate.getTime() <= startDate.getTime()) {
          endDate = new Date(
            startDate.getTime() + durationDays * 24 * 60 * 60 * 1000
          );
        }

        projectStart = projectStart ? (startDate < projectStart ? startDate : projectStart) : startDate;
        projectEnd = projectEnd ? (endDate > projectEnd ? endDate : projectEnd) : endDate;

        const status = (m.status || '').toLowerCase();
        if (status === 'completed') {
          milestoneCompletion.done += 1;
        }
        milestoneCompletion.total += 1;

        const color = getStatusColorValue(status || project.projectStatus);
        const milestoneName =
          m.name || (m as any).stepName || (m as any).title || `Milestone ${idx + 1}`;

        milestoneRows.push({
          projectId: project.projectId,
          projectName: project.projectName,
          laneId: `${project.projectId}::milestone::${m.milestoneId || idx}`,
          laneLabel: `${project.projectName} — ${milestoneName}`,
          start: startDate,
          end: endDate,
          startValue: startDate.getTime(),
          endValue: endDate.getTime(),
          progress: 0,
          color,
          status,
          milestoneName,
          project,
        });
      });
    }

    // Fallback: one bar per project span
    const startDate =
      projectStart ||
      toValidDate(project.firstPlannedDate) ||
      toValidDate(project.nextPlannedDate) ||
      toValidDate(project.lastPlannedDate) ||
      toValidDate(project.lastActualDate) ||
      new Date();

    let endDate =
      projectEnd ||
      toValidDate(project.lastPlannedDate) ||
      toValidDate(project.nextPlannedDate) ||
      toValidDate(project.lastActualDate);

    if (!endDate || endDate.getTime() <= startDate.getTime()) {
      endDate = new Date(
        startDate.getTime() + FALLBACK_DURATION_DAYS * 24 * 60 * 60 * 1000
      );
    }

    const progress =
      milestoneCompletion.total > 0
        ? (milestoneCompletion.done / milestoneCompletion.total) * 100
        : project.milestoneCount > 0
        ? (project.completedCount / project.milestoneCount) * 100
        : project.progress ?? 0;

    const color = getStatusColor(project, colorMode);

    const hasMilestones = !!milestones && milestones.length > 0;
    const isCollapsed = hasMilestones && !!collapsed?.has(project.projectId);
    const overallLaneId = `${project.projectId}::overall`;
    // Harvest D4: the lane label carries the project's own meta — a chevron when its
    // milestone lanes can collapse (D3), then "N milestones · X%".
    const chevron = hasMilestones ? (isCollapsed ? '▸ ' : '▾ ') : '';
    const overallMeta = hasMilestones
      ? ` · ${milestoneCompletion.total} milestones · ${Math.round(progress)}%`
      : ` · ${Math.round(progress)}%`;
    const overallLaneLabel = `${chevron}${project.projectName} — Overall${overallMeta}`;
    const projectRows: GanttDataItem[] = [];

    // Overall background bar
    projectRows.push({
      projectId: project.projectId,
      projectName: project.projectName,
      laneId: overallLaneId,
      laneLabel: overallLaneLabel,
      start: startDate,
      end: endDate,
      startValue: startDate.getTime(),
      endValue: endDate.getTime(),
      progress,
      color: '#e0e0e0',
      milestoneName: 'Overall',
      isOverall: true,
      isOverallBackground: true,
      isOverallForeground: false,
      project,
    });

    // Overall completed portion. Harvest D1: the foreground bar wears the COMPUTED colour
    // (status/discipline/owner), not a hardcoded green — before this, the colorMode toggle
    // recoloured the legend but never a milestone-bearing chart.
    const duration = endDate.getTime() - startDate.getTime();
    const completedEndValue = startDate.getTime() + duration * (progress / 100);
    projectRows.push({
      projectId: project.projectId,
      projectName: project.projectName,
      laneId: overallLaneId,
      laneLabel: overallLaneLabel,
      start: startDate,
      end: new Date(completedEndValue),
      startValue: startDate.getTime(),
      endValue: completedEndValue,
      progress,
      color,
      milestoneName: 'Overall',
      isOverall: true,
      isOverallBackground: false,
      isOverallForeground: true,
      project,
    });

    // Harvest D3: a collapsed project shows only its overall lane.
    if (isCollapsed) {
      rows.push(...projectRows);
      return;
    }

    // If no milestone rows, still add a single project span lane
    if (!milestones || milestones.length === 0) {
      projectRows.push({
        projectId: project.projectId,
        projectName: project.projectName,
        laneId: `${project.projectId}::project-span`,
        laneLabel: project.projectName,
        start: startDate,
        end: endDate,
        startValue: startDate.getTime(),
        endValue: endDate.getTime(),
        progress,
        color,
        milestoneName: undefined,
        project,
      });
    } else {
      projectRows.push(...milestoneRows);
    }

    rows.push(...projectRows);
  });

  return rows;
};

/**
 * Get color based on color mode
 */
const getStatusColor = (
  project: ScheduleProjectSummary,
  mode: 'status' | 'discipline' | 'owner'
): string => {
  switch (mode) {
    case 'status':
      return getStatusColorValue(project.projectStatus);
    case 'discipline':
      return getDisciplineColor(project.disciplines?.[0]);
    case 'owner':
      return getOwnerColor(project.ownerId);
    default:
      return '#0066cc';
  }
};

/**
 * Get color for project status
 * Status mode: Green=completed, Red=overdue, Yellow=in progress, Grey=planned
 */
export const getStatusColorValue = (status?: string | null): string => {
  if (!status) return '#9e9e9e'; // Grey for unknown
  const statusLower = status.toLowerCase();
  if (statusLower.includes('completed') || statusLower.includes('done') || statusLower === 'completed') {
    return '#4caf50'; // Green
  }
  if (statusLower.includes('overdue') || statusLower.includes('late') || statusLower === 'overdue') {
    return '#f44336'; // Red
  }
  if (statusLower.includes('progress') || statusLower.includes('active') || statusLower.includes('in_progress')) {
    return '#ffc107'; // Yellow (changed from Orange to match spec)
  }
  if (statusLower.includes('planned') || statusLower.includes('pending') || statusLower === 'planned') {
    return '#9e9e9e'; // Grey (changed from Blue to match spec)
  }
  if (statusLower.includes('blocked')) {
    return '#9c27b0'; // Purple
  }
  return '#9e9e9e'; // Default grey
};

/**
 * Get color for discipline
 * Different color per discipline using hash-based assignment
 */
export const getDisciplineColor = (discipline?: string | null): string => {
  if (!discipline) return '#9e9e9e';
  // Simple hash-based color assignment
  const colors = [
    '#2196f3', // Blue
    '#4caf50', // Green
    '#ff9800', // Orange
    '#9c27b0', // Purple
    '#f44336', // Red
    '#00bcd4', // Cyan
    '#ff5722', // Deep Orange
    '#673ab7', // Deep Purple
  ];
  let hash = 0;
  for (let i = 0; i < discipline.length; i++) {
    hash = discipline.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Get color for owner
 * Different color per owner using hash-based assignment
 */
export const getOwnerColor = (ownerId?: string | null): string => {
  if (!ownerId) return '#9e9e9e';
  // Simple hash-based color assignment
  const colors = [
    '#2196f3', // Blue
    '#4caf50', // Green
    '#ff9800', // Orange
    '#9c27b0', // Purple
    '#f44336', // Red
    '#00bcd4', // Cyan
    '#ff5722', // Deep Orange
    '#673ab7', // Deep Purple
  ];
  let hash = 0;
  for (let i = 0; i < ownerId.length; i++) {
    hash = ownerId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ScheduleTimeline: React.FC<ScheduleTimelineProps> = ({
  projects,
  onProjectClick,
  colorMode = 'status',
  zoomLevel = 'month',
  onZoomChange,
  milestonesByProject,
  isLoading = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const chartRef2 = useRef<am5xy.XYChart | null>(null);
  
  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipProject, setTooltipProject] = useState<ScheduleProjectSummary | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  
  // Debounced tooltip show function
  const showTooltip = useCallback((project: ScheduleProjectSummary, x: number, y: number) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Set tooltip after a short delay to debounce
    hoverTimeoutRef.current = setTimeout(() => {
      setTooltipProject(project);
      setTooltipPosition({ x, y });
      setTooltipVisible(true);
    }, 100); // 100ms debounce
  }, []);
  
  // Hide tooltip function
  const hideTooltip = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setTooltipVisible(false);
    setTooltipProject(null);
  }, []);
  
  // Handle mouse move for tooltip positioning with smart positioning
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (tooltipVisible && tooltipProject) {
      const offsetX = 12;
      const offsetY = 12;
      const tooltipWidth = 350; // Max width from CSS
      const tooltipHeight = 200; // Estimated height
      
      let x = e.clientX + offsetX;
      let y = e.clientY + offsetY;
      
      // Adjust if tooltip would go off right edge
      if (x + tooltipWidth > window.innerWidth) {
        x = e.clientX - tooltipWidth - offsetX;
      }
      
      // Adjust if tooltip would go off left edge
      if (x < 0) {
        x = offsetX;
      }
      
      // Adjust if tooltip would go off bottom edge
      if (y + tooltipHeight > window.innerHeight) {
        y = e.clientY - tooltipHeight - offsetY;
      }
      
      // Adjust if tooltip would go off top edge
      if (y < 0) {
        y = offsetY;
      }
      
      setTooltipPosition({ x, y });
    }
  }, [tooltipVisible, tooltipProject]);
  
  // Track global mouse position for tooltip positioning
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
      if (tooltipVisible && tooltipProject) {
        handleMouseMove(e);
      }
    };
    
    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [tooltipVisible, tooltipProject, handleMouseMove]);

  // Harvest D3: collapsed projects show only their overall lane. State lives here so the
  // chart effect below rebuilds (its dep) — a rebuild loses pan/zoom, the price every
  // structural change already pays.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = useCallback((projectId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  // Count LANES, not bars (harvest D5 — the old count added 2 for the overall lane whose
  // two bars share one lane, and missed the overall lane entirely in the no-milestone case):
  // milestones -> 1 overall lane + one per milestone (or overall only when collapsed);
  // none -> the overall lane + the project-span lane.
  const laneCount = useMemo(() => {
    let total = 0;
    projects.forEach((project) => {
      const ms = milestonesByProject?.[project.projectId];
      if (ms && ms.length > 0) {
        total += collapsed.has(project.projectId) ? 1 : ms.length + 1;
      } else {
        total += 2;
      }
    });
    return total;
  }, [projects, milestonesByProject, collapsed]);

  const chartHeight = useMemo(() => {
    const ROW_HEIGHT = 48; // per lane
    const CHROME = 120;    // x-axis labels + scrollbar minimap
    const MIN_HEIGHT = 240; // harvest D5: a one-project chart no longer floats in 520px
    return Math.max(MIN_HEIGHT, laneCount * ROW_HEIGHT + CHROME);
  }, [laneCount]);

  useEffect(() => {
    if (!chartRef.current || projects.length === 0) {
      return;
    }

    // Create root element
    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;

    // Set themes (guarded for test mocks that may not provide full API)
    root?.setThemes?.([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
      })
    );
    chartRef2.current = chart;

    // Add cursor
    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
      })
    );
    cursor.lineY.set('visible', false);

    // Create axes
    // Y-axis for projects
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'laneId',
        renderer: am5xy.AxisRendererY.new(root, {
          inversed: true,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
      })
    );

    // X-axis for dates
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50,
        }),
      })
    );

    // Transform data
    const ganttData = transformToGanttData(projects, milestonesByProject, colorMode, collapsed);

    // Prepare category data for Y-axis
    const categoryData: {
      laneId: string; laneLabel: string; projectId: string;
      isOverall?: boolean; hasMilestones?: boolean;
    }[] = [];
    const seenLanes = new Set<string>();
    ganttData.forEach((item) => {
      if (!seenLanes.has(item.laneId)) {
        seenLanes.add(item.laneId);
        categoryData.push({
          laneId: item.laneId,
          laneLabel: item.laneLabel,
          projectId: item.projectId,
          isOverall: item.isOverall,
          hasMilestones: !!milestonesByProject?.[item.projectId]?.length,
        });
      }
    });
    yAxis.data.setAll(categoryData);
    const labelsTemplate = yAxis.get('renderer').labels.template;
    labelsTemplate.set('text', '{laneLabel}');
    // Harvest D3: clicking an overall lane's LABEL toggles that project's milestone lanes
    // (the bar itself keeps onProjectClick). Guarded optional calls — the test mock models
    // only what each suite asserts, the root?.setThemes precedent.
    labelsTemplate.setAll?.({ cursorOverStyle: 'pointer' });
    labelsTemplate.events?.on?.('click', (ev: any) => {
      const dc = ev.target?.dataItem?.dataContext as any;
      if (dc?.isOverall && dc?.hasMilestones && dc?.projectId) {
        toggleCollapse(dc.projectId);
      }
    });

    // Harvest D2: a Today marker on the date axis — the one orientation cue every Gantt
    // reader looks for first.
    const todayItem = (xAxis as any).makeDataItem?.({ value: Date.now() });
    if (todayItem) {
      const todayRange = (xAxis as any).createAxisRange?.(todayItem);
      todayRange?.get?.('grid')?.setAll?.({
        stroke: am5.color('#e53935'),
        strokeWidth: 2,
        strokeOpacity: 0.85,
        strokeDasharray: [4, 3],
        visible: true,
      });
      todayRange?.get?.('label')?.setAll?.({
        text: 'Today',
        fill: am5.color('#e53935'),
        fontSize: 11,
        inside: true,
      });
    }

    // Create a single series for all projects
    // For Gantt chart, we use ColumnSeries with date ranges
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Projects',
        xAxis: xAxis,
        yAxis: yAxis,
        baseAxis: yAxis,
        openValueXField: 'startValue',
        valueXField: 'endValue',
        categoryYField: 'laneId',
      })
    );

    // Configure column appearance
    series.columns.template.setAll({
      // Disable default AmCharts tooltip - we'll use custom React tooltip
      tooltipText: '',
      strokeOpacity: 0.8,
      fillOpacity: 0.7,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBL: 4,
      cornerRadiusBR: 4,
      strokeWidth: 2,
    });
    
    // Add hover handlers for custom tooltip
    series.columns.template.events.on('pointerover', (ev) => {
      const dataItem = ev.target.dataItem?.dataContext as any;
      if (dataItem?.project) {
        const project = dataItem.project as ScheduleProjectSummary;
        // Use last known mouse position (tracked globally)
        const { x, y } = lastMousePositionRef.current;
        showTooltip(project, x, y);
      }
    });
    
    series.columns.template.events.on('pointerout', () => {
      hideTooltip();
    });
    
    // Also handle on root container to hide tooltip when mouse leaves
    root.container.events.on('pointerout', () => {
      hideTooltip();
    });

    // Set colors based on data
    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem?.dataContext as any;
      if (dataItem?.color) {
        return am5.color(dataItem.color);
      }
      return fill;
    });

    series.columns.template.adapters.add('stroke', (stroke, target) => {
      const dataItem = target.dataItem?.dataContext as any;
      if (dataItem?.color) {
        return am5.color(dataItem.color);
      }
      return stroke;
    });

    series.columns.template.adapters.add('fillOpacity', (opacity, target) => {
      const dataItem = target.dataItem?.dataContext as any;
      if (dataItem?.isOverallBackground) {
        return 0.35;
      }
      if (dataItem?.isOverallForeground) {
        return 0.9;
      }
      return 0.7;
    });

    // Add click handler
    series.columns.template.events.on('click', (ev) => {
      const dataItem = ev.target.dataItem?.dataContext as any;
      if (dataItem?.project && onProjectClick) {
        onProjectClick(dataItem.project);
      }
    });

    // Format data for series
    // AmCharts DateAxis expects Date objects or timestamps
    const seriesData = ganttData.map((item) => {
      return {
        laneId: item.laneId,
        laneLabel: item.laneLabel,
        projectName: item.projectName,
        projectId: item.projectId,
        startValue: item.startValue, // numeric timestamp for DateAxis
        endValue: item.endValue,
        startFormatted: item.start.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        endFormatted: item.end.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        progress: item.progress.toFixed(0),
        milestoneCount: item.project.milestoneCount,
        completedCount: item.project.completedCount,
        overdueCount: item.project.overdueCount,
        color: item.color,
        status: item.status,
        milestoneName: item.milestoneName,
        isOverall: item.isOverall,
        isOverallBackground: item.isOverallBackground,
        isOverallForeground: item.isOverallForeground,
        project: item.project,
      };
    });

    series.data.setAll(seriesData);

    // Add scrollbar
    chart.set(
      'scrollbarX',
      am5xy.XYChartScrollbar.new(root, {
        orientation: 'horizontal',
        height: 50,
      })
    );

    // Set zoom level
    if (zoomLevel === 'week') {
      xAxis.zoomToDates(
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      );
    } else if (zoomLevel === 'month') {
      xAxis.zoomToDates(
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      );
    } else if (zoomLevel === 'quarter') {
      xAxis.zoomToDates(
        new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
      );
    }

    // Cleanup function
    return () => {
      hideTooltip();
      root.dispose();
    };
    // milestonesByProject/collapsed in the deps (harvest hygiene): the old array omitted
    // milestonesByProject, masked only because SchedulePage's unmemoized click handler forced
    // a rebuild every render — the exact pan/zoom-loss bug ProjectScheduleTab works around.
  }, [projects, colorMode, zoomLevel, onProjectClick, showTooltip, hideTooltip,
    milestonesByProject, collapsed, toggleCollapse]);

  if (projects.length === 0) {
    return (
      <div className="schedule-timeline-empty">
        <p>No projects to display</p>
      </div>
    );
  }

  return (
    <div className="schedule-timeline-container">
      <div
        ref={chartRef}
        className="schedule-timeline-chart"
        style={{ height: `${chartHeight}px` }}
      >
        {isLoading && <div className="schedule-timeline-loading">Loading milestones…</div>}
      </div>
      
      {/* Custom Tooltip */}
      {tooltipProject && (
        <ScheduleTooltip
          project={tooltipProject}
          position={tooltipPosition}
          visible={tooltipVisible}
        />
      )}
    </div>
  );
};

export default ScheduleTimeline;

